import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Segmented,
  Space,
  Typography,
  Tag,
  Button,
  Tooltip,
  Spin,
  Row,
  Col,
  Empty,
  Modal,
  Form,
  Select,
  message,
  Grid,
  Dropdown,
} from "antd";
import {
  WhatsAppOutlined,
  PlusOutlined,
  CopyOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  MoreOutlined,
  DownOutlined,
  UpOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  getallENQAdmin,
  getSkuList,
  createAdminEnquiry,
  searchAdminUsers,
  linkAdminEnquiry,
} from "../services/api";
import Cookies from "js-cookie";
import "./AdminEnquiry.css";
import {
  STATUS_COLORS,
  PAYMENT_COLORS,
  normalizeEnquiries,
} from "../components/utils";

const { Search } = Input;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const getFilterOptions = (values) =>
  [
    ...new Set(
      values.filter((value) => value != null && String(value).trim() !== ""),
    ),
  ].map((value) => ({ text: String(value), value: String(value) }));

const maskEmail = (email = "") => {
  if (!email) return "";
  const [user, domain] = email.split("@");
  if (!domain) return email;
  if (user.length <= 2) return `${user[0] || "*"}***@${domain}`;
  return `${user.slice(0, 4)}***@${domain}`;
};

const maskPhone = (phone = "") => {
  if (!phone) return "";
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length <= 4) return "*".repeat(digits.length);
  return `${"*".repeat(digits.length - 4)}${digits.slice(-4)}`;
};

const AdminEnquiry = () => {
  const [dealFilter, setDealFilter] = useState("open");
  const [emailQuery, setEmailQuery] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState("");
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState(null);
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [mobileSort, setMobileSort] = useState("created-desc");
  const [mobileSku, setMobileSku] = useState(undefined);
  const [mobileStatus, setMobileStatus] = useState(undefined);
  const [mobilePayment, setMobilePayment] = useState(undefined);
  const [mobileLink, setMobileLink] = useState(undefined);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const accessToken = Cookies.get("token");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!accessToken) navigate("/");
  }, [accessToken, navigate]);
  const { data: userSearchData, isFetching: searchingUsers } = useQuery({
    queryKey: ["admin-user-search", linkQuery],
    queryFn: () => searchAdminUsers(linkQuery, accessToken),
    enabled: linkOpen && linkQuery.trim().length >= 2 && !!accessToken,
  });
  useEffect(() => {
    if (!isMobile) {
      setShowBackToTop(false);
      return;
    }

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 250);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);
  const linkMutation = useMutation({
    mutationFn: () =>
      linkAdminEnquiry(selectedEnquiry.enqNo, selectedUserEmail, accessToken),
    onSuccess: (result) => {
      message.success(result.message || "Enquiry linked successfully");
      setLinkOpen(false);
      setLinkQuery("");
      setSelectedEnquiry(null);
      setSelectedUserEmail(null);
      queryClient.invalidateQueries({ queryKey: ["allENQ"] });
    },
    onError: (error) => {
      message.error(error.error || error.message || "Unable to link enquiry");
    },
  });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["allENQ", { dealFilter, emailQuery }],
    enabled: !!accessToken,
    queryFn: async () => {
      const params = new URLSearchParams();

      if (dealFilter === "open") params.append("deal", "open");
      if (emailQuery) params.append("q", emailQuery);

      const queryString = params.toString() ? `?${params.toString()}` : "";

      return getallENQAdmin(queryString, accessToken);
    },
    keepPreviousData: true,
  });

  const { data: skuData, isLoading: skuLoading } = useQuery({
    queryKey: ["sku-list"],
    queryFn: getSkuList,
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  const skuOptions = useMemo(
    () =>
      (skuData?.items || []).map((item) => ({
        label: `${item.sku} — ${item.title}`,
        value: item.sku,
      })),
    [skuData],
  );

  const skuTitleMap = useMemo(
    () =>
      (skuData?.items || []).reduce((acc, item) => {
        acc[item.sku] = item.title;
        return acc;
      }, {}),
    [skuData],
  );

  const enquiries = normalizeEnquiries(data?.enquiries || []);

  const createMutation = useMutation({
    mutationFn: (values) =>
      createAdminEnquiry(
        {
          name: values.name.trim(),
          phoneNo: values.phoneNo.trim(),
          email: values.email?.trim() || "",
          sku: values.sku,
          title: skuTitleMap[values.sku],
        },
        accessToken,
      ),
    onSuccess: async (result) => {
      messageApi.success(`Enquiry ${result.enquiryId} created successfully`);

      setCreateOpen(false);
      form.resetFields();

      await queryClient.invalidateQueries({ queryKey: ["allENQ"] });
    },
    onError: (error) => {
      messageApi.error(
        error?.error || error?.message || "Unable to create enquiry",
      );
    },
  });

  const columns = useMemo(
    () => [
      {
        title: "ENQ No",
        dataIndex: "enqNo",
        key: "enqNo",
        width: 130,
        sorter: (a, b) =>
          String(a.enqNo || "").localeCompare(
            String(b.enqNo || ""),
            undefined,
            { numeric: true, sensitivity: "base" },
          ),
        filters: getFilterOptions(enquiries.map((e) => e.enqNo)),
        onFilter: (value, record) => String(record.enqNo || "") === value,
        render: (value) => (
          <Text style={{ fontSize: 12 }} ellipsis>
            {value || "—"}
          </Text>
        ),
      },
      {
        title: "SKU",
        dataIndex: "sku",
        key: "sku",
        width: 160,
        sorter: (a, b) =>
          String(a.sku || "").localeCompare(String(b.sku || "")),
        filters: getFilterOptions(enquiries.map((e) => e.sku)),
        onFilter: (value, record) => String(record.sku || "") === value,
        render: (value) => (
          <Text style={{ fontSize: 12 }} ellipsis>
            {value || "—"}
          </Text>
        ),
      },
      {
        title: "Deal",
        dataIndex: "deal",
        key: "deal",
        width: 100,
        filters: [
          { text: "Open", value: "open" },
          { text: "Closed", value: "closed" },
        ],
        onFilter: (value, record) =>
          String(record.deal || "open").toLowerCase() === value,
        sorter: (a, b) =>
          String(a.deal || "open").localeCompare(String(b.deal || "open")),
        render: (deal) => {
          const value = (deal || "open").toLowerCase();
          return (
            <Tag color={value === "closed" ? "green" : "orange"}>
              {value === "closed" ? "Closed" : "Open"}
            </Tag>
          );
        },
      },
      {
        title: "Customer",
        dataIndex: "name",
        key: "name",
        sorter: (a, b) =>
          String(a.name || "").localeCompare(String(b.name || "")),
        filters: getFilterOptions(enquiries.map((e) => e.name)),
        onFilter: (value, record) => String(record.name || "") === value,
        render: (_, record) => {
          const contact = record.email || record.phoneNo || "";
          const masked = record.email
            ? maskEmail(record.email)
            : maskPhone(record.phoneNo);
          return (
            <div>
              <div>{record.name || "—"}</div>
              <Space size={4}>
                <Tooltip
                  title={
                    <div>
                      {record.email && <div>{record.email}</div>}
                      {record.phoneNo && <div>{record.phoneNo}</div>}
                    </div>
                  }
                >
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      cursor: contact ? "pointer" : "default",
                    }}
                  >
                    {masked || "No contact details"}
                  </Text>
                </Tooltip>
                {contact && (
                  <Tooltip title="Copy contact">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={async (event) => {
                        event.stopPropagation();
                        try {
                          await navigator.clipboard.writeText(contact);
                          message.success("Copied to clipboard");
                        } catch {
                          message.error("Unable to copy");
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            </div>
          );
        },
      },
      {
        title: "WhatsApp",
        dataIndex: "phoneNo",
        key: "phoneNo",
        width: 115,
        filters: getFilterOptions(enquiries.map((e) => e.phoneNo)),
        onFilter: (value, record) => String(record.phoneNo || "") === value,
        render: (phone) => {
          const cleanPhone = String(phone || "").replace(/[^\d]/g, "");
          return (
            <Tooltip title="Open WhatsApp">
              <Button
                size="small"
                type="text"
                icon={<WhatsAppOutlined />}
                disabled={!cleanPhone}
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(
                    `https://wa.me/${cleanPhone}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                Chat
              </Button>
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filters: getFilterOptions(enquiries.map((e) => e.status || "Created")),
        onFilter: (value, record) =>
          String(record.status || "Created") === value,
        sorter: (a, b) =>
          String(a.status || "Created").localeCompare(
            String(b.status || "Created"),
          ),
        render: (status) => (
          <Tag color={STATUS_COLORS[status] || "default"}>
            {status || "Created"}
          </Tag>
        ),
      },
      {
        title: "Payment",
        dataIndex: "paymentStatus",
        key: "paymentStatus",
        filters: getFilterOptions(enquiries.map((e) => e.paymentStatus || "—")),
        onFilter: (value, record) =>
          String(record.paymentStatus || "—") === value,
        sorter: (a, b) =>
          String(a.paymentStatus || "").localeCompare(
            String(b.paymentStatus || ""),
          ),
        render: (payment) => (
          <Tag color={PAYMENT_COLORS[payment] || "default"}>
            {payment || "—"}
          </Tag>
        ),
      },
      {
        title: "Created",
        dataIndex: "createdAtDate",
        key: "createdAtDate",
        width: 130,
        sorter: (a, b) =>
          (a.createdAtDate?.getTime?.() || 0) -
          (b.createdAtDate?.getTime?.() || 0),
        defaultSortOrder: "descend",
        filters: getFilterOptions(
          enquiries.map((e) =>
            e.createdAtDate ? dayjs(e.createdAtDate).format("DD/MM/YYYY") : "—",
          ),
        ),
        onFilter: (value, record) =>
          (record.createdAtDate
            ? dayjs(record.createdAtDate).format("DD/MM/YYYY")
            : "—") === value,
        render: (_, record) =>
          record.createdAtDate
            ? dayjs(record.createdAtDate).format("DD/MM/YYYY")
            : "—",
      },
      {
        title: "Link",
        key: "link",
        responsive: ["md"],
        filters: [
          { text: "Linked", value: "linked" },
          { text: "Not Linked", value: "unlinked" },
        ],
        onFilter: (value, record) => {
          const linked = Boolean(record.userId || record.linkedAt);
          return value === "linked" ? linked : !linked;
        },
        render: (_, record) => {
          const isLinked = Boolean(record.userId || record.linkedAt);
          if (isLinked) return <Tag color="green">Linked</Tag>;
          return (
            <Button
              size="small"
              hidden={record.createdBy === "USER"}
              onClick={(event) => {
                event.stopPropagation();
                setSelectedEnquiry(record);
                setSelectedUserEmail(null);
                setLinkQuery("");
                setLinkOpen(true);
              }}
            >
              Link Enquiry
            </Button>
          );
        },
      },
    ],
    [enquiries],
  );

  const mobileEnquiries = useMemo(() => {
    let rows = [...enquiries];
    if (mobileSku) rows = rows.filter((e) => String(e.sku || "") === mobileSku);
    if (mobileStatus)
      rows = rows.filter((e) => String(e.status || "Created") === mobileStatus);
    if (mobilePayment)
      rows = rows.filter(
        (e) => String(e.paymentStatus || "—") === mobilePayment,
      );
    if (mobileLink)
      rows = rows.filter(
        (e) => (mobileLink === "linked") === Boolean(e.userId || e.linkedAt),
      );
    rows.sort((a, b) => {
      if (mobileSort === "enq-asc" || mobileSort === "enq-desc") {
        const result = String(a.enqNo || "").localeCompare(
          String(b.enqNo || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        );
        return mobileSort === "enq-asc" ? result : -result;
      }
      const result =
        (a.createdAtDate?.getTime?.() || 0) -
        (b.createdAtDate?.getTime?.() || 0);
      return mobileSort === "created-asc" ? result : -result;
    });
    return rows;
  }, [
    enquiries,
    mobileSku,
    mobileStatus,
    mobilePayment,
    mobileLink,
    mobileSort,
  ]);

  if (isLoading) {
    return (
      <Row justify="center" style={{ minHeight: "60vh", padding: 16 }}>
        <Col>
          <Spin />
        </Col>
      </Row>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 1600, margin: "0 auto" }}>
      {contextHolder}

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Row align="middle" justify="space-between" gutter={[12, 12]}>
          <Col>
            <Text strong style={{ fontSize: 20 }}>
              Enquiry Dashboard
            </Text>
            <br />
            <Text type="secondary">
              Manage customer enquiries and booking payments
            </Text>
          </Col>

          <Col style={{ display: isMobile ? "none" : undefined }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              Create Enquiry
            </Button>
          </Col>
        </Row>

        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={14}>
            <Search
              allowClear
              placeholder="Search enquiries"
              value={emailInput}
              onSearch={(value) => {
                const trimmed = value.trim();
                setEmailInput(trimmed);
                setEmailQuery(trimmed);
              }}
              onChange={(event) => {
                const value = event.target.value;
                setEmailInput(value);
                if (!value) setEmailQuery("");
              }}
            />
          </Col>

          <Col xs={24} md={10}>
            <Segmented
              block
              options={[
                { label: "All Enquiries", value: "all" },
                { label: "Open Deals", value: "open" },
              ]}
              value={dealFilter}
              onChange={setDealFilter}
            />
          </Col>
        </Row>

        {isError ? (
          <Empty description="Unable to load enquiries" />
        ) : enquiries.length === 0 ? (
          <Empty description="No enquiries yet">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              Create your first enquiry
            </Button>
          </Empty>
        ) : isMobile ? (
          <div className="mobile-enquiry-view">
            <div className="mobile-enquiry-tools">
              <Select
                size="small"
                value={mobileSort}
                onChange={setMobileSort}
                aria-label="Sort enquiries"
                options={[
                  { label: `Created Date (Desc)↓`,value: "created-desc"},
                  { label: `Created Date (Asc)↑`, value: "created-asc" },
                  { label: `ENQ (Desc)↓`, value: "enq-asc" },
                  { label: `ENQ (Asc)↑`, value: "enq-desc" },
                ]}
                suffixIcon={<SortAscendingOutlined />}
              />

              <Dropdown
                size="small"
                trigger={["click"]}
                placement="bottomRight"
                open={mobileFiltersOpen}
                onOpenChange={setMobileFiltersOpen}
                popupRender={() => (
                  <div
                    style={{
                      width: 280,
                      padding: 16,
                      background: "#fff",
                      borderRadius: 10,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Space
                      direction="vertical"
                      size="middle"
                      style={{ width: "100%" }}
                    >
                      <strong>More Filters</strong>

                      <div>
                        <div style={{ marginBottom: 6 }}>SKU / Service</div>
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="All services"
                          style={{ width: "100%" }}
                          value={mobileSku}
                          onChange={setMobileSku}
                          options={getFilterOptions(
                            enquiries.map((e) => e.sku),
                          ).map((o) => ({
                            label: o.text,
                            value: o.value,
                          }))}
                        />
                      </div>

                      <div>
                        <div style={{ marginBottom: 6 }}>Enquiry Status</div>
                        <Select
                          allowClear
                          placeholder="All statuses"
                          style={{ width: "100%" }}
                          value={mobileStatus}
                          onChange={setMobileStatus}
                          options={getFilterOptions(
                            enquiries.map((e) => e.status || "Created"),
                          ).map((o) => ({
                            label: o.text,
                            value: o.value,
                          }))}
                        />
                      </div>

                      <div>
                        <div style={{ marginBottom: 6 }}>Payment Status</div>
                        <Select
                          allowClear
                          placeholder="All payments"
                          style={{ width: "100%" }}
                          value={mobilePayment}
                          onChange={setMobilePayment}
                          options={getFilterOptions(
                            enquiries.map((e) => e.paymentStatus || "—"),
                          ).map((o) => ({
                            label: o.text,
                            value: o.value,
                          }))}
                        />
                      </div>

                      <div>
                        <div style={{ marginBottom: 6 }}>Link Status</div>
                        <Select
                          allowClear
                          placeholder="All link statuses"
                          style={{ width: "100%" }}
                          value={mobileLink}
                          onChange={setMobileLink}
                          options={[
                            { label: "Linked", value: "linked" },
                            { label: "Not Linked", value: "unlinked" },
                          ]}
                        />
                      </div>

                      <Space style={{ width: "100%" }}>
                        <Button
                          onClick={() => {
                            setMobileSku(undefined);
                            setMobileStatus(undefined);
                            setMobilePayment(undefined);
                            setMobileLink(undefined);
                            setMobileFiltersOpen(false);
                          }}
                        >
                          Reset
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => setMobileFiltersOpen(false)}
                        >
                          Done
                        </Button>
                      </Space>
                    </Space>
                  </div>
                )}
              >
                <Button icon={<FilterOutlined />}>
                  More Filters
                  <DownOutlined />
                </Button>
              </Dropdown>
            </div>

            {mobileEnquiries.length ? (
              mobileEnquiries.map((record) => {
                const cleanPhone = String(record.phoneNo || "").replace(
                  /[^\d]/g,
                  "",
                );
                const contact = record.email || record.phoneNo || "";
                const actionItems = [
                  {
                    key: "copy",
                    label: "Copy contact",
                    icon: <CopyOutlined />,
                    disabled: !contact,
                    onClick: async () => {
                      try {
                        await navigator.clipboard.writeText(contact);
                        message.success("Copied to clipboard");
                      } catch {
                        message.error("Unable to copy");
                      }
                    },
                  },
                  {
                    key: "whatsapp",
                    label: "Open WhatsApp",
                    icon: <WhatsAppOutlined />,
                    disabled: !cleanPhone,
                    onClick: () =>
                      window.open(
                        `https://wa.me/${cleanPhone}`,
                        "_blank",
                        "noopener,noreferrer",
                      ),
                  },
                  ...(!record.userId &&
                  !record.linkedAt &&
                  record.createdBy !== "USER"
                    ? [
                        {
                          key: "link",
                          label: "Link Enquiry",
                          onClick: () => {
                            setSelectedEnquiry(record);
                            setSelectedUserEmail(null);
                            setLinkQuery("");
                            setLinkOpen(true);
                          },
                        },
                      ]
                    : []),
                ];
                return (
                  <div
                    className="mobile-enquiry-card"
                    key={record.id || record.enqNo}
                    onClick={() => navigate(`/admin/enquiries/${record.enqNo}`)}
                  >
                    <div className="mobile-enquiry-card-top">
                      <div className="mobile-enquiry-customer">
                        {record.name || "—"}
                      </div>
                      <Dropdown
                        trigger={["click"]}
                        menu={{ items: actionItems }}
                      >
                        <Button
                          type="text"
                          icon={<MoreOutlined />}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Actions for ${record.enqNo}`}
                        />
                      </Dropdown>
                    </div>
                    <div className="mobile-enquiry-meta">
                      {record.createdAtDate
                        ? dayjs(record.createdAtDate).format("DD/MM/YYYY")
                        : "—"}
                      <span>•</span>
                      <strong>{record.enqNo || "—"}</strong>
                    </div>
                    <div className="mobile-enquiry-sku">
                      {record.sku || "—"}
                    </div>
                    <div className="mobile-enquiry-tags">
                      <Tag color={STATUS_COLORS[record.status] || "default"}>
                        {record.status || "Created"}
                      </Tag>
                      <Tag
                        color={
                          PAYMENT_COLORS[record.paymentStatus] || "default"
                        }
                      >
                        {record.paymentStatus || "—"}
                      </Tag>
                      <Tag
                        color={
                          (record.deal || "open").toLowerCase() === "closed"
                            ? "green"
                            : "orange"
                        }
                      >
                        {(record.deal || "open").toLowerCase() === "closed"
                          ? "Closed"
                          : "Open"}
                      </Tag>
                    </div>
                  </div>
                );
              })
            ) : (
              <Empty description="No enquiries match these filters" />
            )}
            <div className="mobile-enquiry-total">
              {mobileEnquiries.length} enquiries
            </div>
          </div>
        ) : (
          <Table
            size="middle"
            loading={isLoading}
            columns={columns}
            dataSource={enquiries}
            showSorterTooltip
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              responsive: true,
              showTotal: (total) => `Total ${total} enquiries`,
            }}
            rowKey={(record) => record.id || record.enqNo}
            tableLayout="auto"
            scroll={{ x: 1200 }}
            onRow={(record) => ({
              onClick: () => navigate(`/admin/enquiries/${record.enqNo}`),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </Space>

      <Modal
        title="Create Enquiry"
        open={createOpen}
        onCancel={() => {
          if (!createMutation.isPending) {
            setCreateOpen(false);
            form.resetFields();
          }
        }}
        footer={null}
        destroyOnClose
        width={520}
      >
        <Text type="secondary">
          Create a lead from WhatsApp or another contact channel. An account is
          not required at this stage.
        </Text>

        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
          onFinish={(values) => createMutation.mutate(values)}
        >
          <Form.Item
            label="Customer name"
            name="name"
            rules={[
              { required: true, message: "Enter the customer name" },
              { whitespace: true, message: "Enter the customer name" },
            ]}
          >
            <Input
              placeholder="Customer full name"
              maxLength={100}
              autoComplete="name"
            />
          </Form.Item>

          <Form.Item
            label="WhatsApp number"
            name="phoneNo"
            extra="Include the country code, e.g. +91 98765 43210."
            rules={[
              { required: true, message: "Enter a WhatsApp number" },
              {
                validator: (_, value) => {
                  if (!value || /^[+()\d\s-]{7,20}$/.test(value.trim())) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Enter a valid phone number"),
                  );
                },
              },
            ]}
          >
            <Input
              placeholder="+91 98765 43210"
              maxLength={20}
              inputMode="tel"
              autoComplete="tel"
            />
          </Form.Item>

          <Form.Item
            label="Email (optional)"
            name="email"
            rules={[{ type: "email", message: "Enter a valid email address" }]}
          >
            <Input
              placeholder="customer@example.com"
              maxLength={150}
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            label="Service"
            name="sku"
            rules={[{ required: true, message: "Select a service" }]}
          >
            <Select
              showSearch
              loading={skuLoading}
              placeholder="Select a service or package"
              options={skuOptions}
              optionFilterProp="label"
              notFoundContent={
                skuLoading ? "Loading services..." : "No services found"
              }
            />
          </Form.Item>

          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={createMutation.isPending}
            >
              Create Enquiry
            </Button>

            <Button
              block
              disabled={createMutation.isPending}
              onClick={() => {
                setCreateOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Space>
        </Form>
      </Modal>
      <Modal
        title={`Link ${selectedEnquiry?.enqNo || "Enquiry"} to a User`}
        open={linkOpen}
        onCancel={() => {
          setLinkOpen(false);
          setLinkQuery("");
          setSelectedEnquiry(null);
          setSelectedUserEmail(null);
        }}
        onOk={() => linkMutation.mutate()}
        okText="Link Enquiry"
        confirmLoading={linkMutation.isPending}
        okButtonProps={{ disabled: !selectedUserEmail }}
        destroyOnClose
      >
        <Input
          placeholder="Search by name, email, or phone"
          value={linkQuery}
          onChange={(event) => {
            setLinkQuery(event.target.value);
            setSelectedUserEmail(null);
          }}
          allowClear
        />

        <div style={{ marginTop: 16 }}>
          {linkQuery.trim().length < 2 ? (
            <Typography.Text type="secondary">
              Enter at least 2 characters to search registered users.
            </Typography.Text>
          ) : searchingUsers ? (
            <Spin />
          ) : (
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {(userSearchData?.users || []).map((user) => (
                <div
                  key={user.email}
                  onClick={() => setSelectedUserEmail(user.email)}
                  style={{
                    padding: 12,
                    marginTop: 8,
                    border: "1px solid",
                    borderColor:
                      selectedUserEmail === user.email ? "#1677ff" : "#d9d9d9",
                    borderRadius: 8,
                    cursor: "pointer",
                    background:
                      selectedUserEmail === user.email
                        ? "#e6f4ff"
                        : "transparent",
                  }}
                >
                  <Typography.Text strong>
                    {[user.firstName, user.lastName]
                      .filter(Boolean)
                      .join(" ") || "Unnamed user"}
                  </Typography.Text>
                  <br />
                  <Typography.Text>{user.email}</Typography.Text>
                  {user.phoneNo && (
                    <>
                      <br />
                      <Typography.Text type="secondary">
                        {user.phoneNo}
                      </Typography.Text>
                    </>
                  )}
                </div>
              ))}

              {userSearchData?.users?.length === 0 && (
                <Empty description="No registered users found" />
              )}
            </div>
          )}
        </div>

        {selectedUserEmail && (
          <Typography.Paragraph style={{ marginTop: 16 }}>
            Selected user:{" "}
            <Typography.Text strong>{selectedUserEmail}</Typography.Text>
          </Typography.Paragraph>
        )}
      </Modal>
      {isMobile && (
        <div className="mobile-floating-actions">
          {showBackToTop && (
            <Button
              className="mobile-back-to-top"
              type="default"
              shape="circle"
              size="large"
              icon={<UpOutlined />}
              aria-label="Back to top"
              onClick={() =>
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                })
              }
            />
          )}

          <Button
            className="mobile-create-enquiry"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            Create Enquiry
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminEnquiry;
